import { PreferencesState } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  Component,
  DestroyRef,
  ElementRef,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MatCalendar,
  MatRangeDateSelectionModel,
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { deepEqual } from 'fast-equals';
import { distinctUntilChanged } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { FormRowDirective } from '../../directives/form-row.directive';
import { DatePipe, formatDate } from '../../pipes/date.pipe';
import { ButtonGroup } from '../button-group/button-group.component';
import { Button } from '../button/button.component';
import { Card } from '../card/card.component';
import { FilterItem } from '../filter-item/filter-item.component';
import { ShowField } from '../show-field/show-field.component';

type RangeGroup = FormGroup<{
  start: FormControl<Date | null>;
  end: FormControl<Date | null>;
}>;

const initialValues = {
  start: null,
  end: null,
};

@Component({
  selector: 'procure-filter-date-range',
  templateUrl: './filter-date-range.component.html',
  styleUrl: './filter-date-range.component.scss',
  imports: [
    ReactiveFormsModule,
    Button,
    FormRowDirective,
    ButtonGroup,
    MatIcon,
    ScrollingModule,
    Card,
    ShowField,
    DatePipe,
    MatTooltip,
    MatCalendar,
  ],
  providers: [
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
    { provide: FilterItem, useExisting: FilterDateRange },
  ],
})
export class FilterDateRange extends FilterItem {
  readonly container =
    viewChild.required<ElementRef<HTMLDivElement>>('container');

  readonly overlay = viewChild<ElementRef>('overlay');

  readonly group = input.required<RangeGroup>();
  readonly hint = input<string>('');
  readonly label = input.required<string>();
  readonly range = input<boolean>(true);

  readonly tooltip = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly primary = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly startSelection = output<void>();
  readonly endSelection = output<void>();

  protected selectedDateRange: DateRange<Date> | undefined;

  protected readonly activeColor = computed(() =>
    this.primary() ? 'primary' : 'black',
  );

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly start = signal<Date | null>(null);
  protected readonly end = signal<Date | null>(null);
  protected readonly hasValue = computed(() => this.start() && this.end());
  protected readonly isOpened = signal(false);
  protected readonly id = uuid();
  protected readonly alignment = signal('left');
  protected readonly dateFormat = inject(PreferencesState).dateFormat;

  protected readonly remainder = computed(() => {
    if (!this.hasValue()) {
      return null;
    }

    return `${formatDate(this.start(), 'shortDate', this.dateFormat())} - ${formatDate(this.end(), 'shortDate', this.dateFormat())}`;
  });

  private ignoreClick: boolean = true;
  private readonly selectionModel = inject(MatRangeDateSelectionModel<Date>);

  private readonly selectionStrategy = inject(
    DefaultMatCalendarRangeStrategy<Date>,
  );

  private readonly destroyRef = inject(DestroyRef);

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    super();

    this.onGlobalClick = this.onGlobalClick.bind(this);

    effect(() => {
      const group = this.group();
      const { value } = this.group();

      if (!value) {
        return;
      }

      this.selectedDateRange = new DateRange<Date>(
        value.start ?? null,
        value.end ?? null,
      );

      group.controls.start.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          distinctUntilChanged((a, b) => deepEqual(a, b)),
        )
        .subscribe((start) => {
          this.start.set(start);
        });

      group.controls.end.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          distinctUntilChanged((a, b) => deepEqual(a, b)),
        )
        .subscribe((end) => {
          this.end.set(end);
        });

      group.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.selectedDateRange = new DateRange<Date>(
            group.controls.start.value,
            group.controls.end.value,
          );
        });
    });

    effect((cleanup) => {
      if (this.isOpened()) {
        this.checkPosition();
        document.addEventListener('click', this.onGlobalClick);
      } else {
        document.removeEventListener('click', this.onGlobalClick);
      }

      cleanup(() => {
        document.removeEventListener('click', this.onGlobalClick);
      });
    });

    effect(() => {
      if (!this.start()) {
        return;
      }

      this.startSelection.emit();
    });

    effect(() => {
      if (!this.end()) {
        return;
      }

      this.endSelection.emit();
    });
  }

  protected selectedRangeChange(selectedDate: Date): void {
    const { selection } = this.selectionModel;
    const newSelection = this.selectionStrategy.selectionFinished(
      selectedDate,
      selection,
    );

    this.selectionModel.updateSelection(newSelection, this);

    this.selectedDateRange = new DateRange<Date>(
      newSelection.start,
      newSelection.end,
    );

    this.group().patchValue({
      start: newSelection.start,
      end: newSelection.end,
    });
  }

  protected toggleOverlay(): void {
    this.isOpened.update((value) => !value);
  }

  protected reset(): void {
    this.group().setValue(initialValues);
    this.selectedDateRange = new DateRange<Date>(null, null);
  }

  private onGlobalClick(event: MouseEvent): void {
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }

    let element = event.target as HTMLElement;
    let isDatePicking = false;

    while (element && element.id !== this.id) {
      isDatePicking =
        element.tagName === 'MAT-MULTI-YEAR-VIEW' ||
        element.tagName === 'MAT-YEAR-VIEW';

      element = element.parentElement as HTMLElement;
    }

    if (element || isDatePicking) {
      return;
    }

    this.isOpened.set(false);
    document.removeEventListener('click', this.onGlobalClick);
  }

  private checkPosition(): void {
    const { nativeElement } = this.container();

    const { left } = nativeElement.getBoundingClientRect();
    const width = Math.max(nativeElement.offsetWidth, 320);

    this.alignment.set(
      left + width > document.body.offsetWidth ? 'right' : 'left',
    );
  }
}
